/**
 * Google Maps Autocomplete Component
 * A reusable component for location search with Google Maps Places Autocomplete
 * 
 * Uses google.maps.places.Autocomplete (JavaScript API - recommended for client-side)
 * 
 * Note: The "Places API (New)" warning refers to server-side REST API, not JavaScript API.
 * For client-side autocomplete, google.maps.places.Autocomplete is the correct approach.
 * 
 * To reduce warnings:
 * 1. Enable "Places API (New)" in Google Cloud Console (for server-side compatibility)
 * 2. Keep "Places API" enabled (required for JavaScript Autocomplete)
 * 
 * See: https://developers.google.com/maps/documentation/javascript/places-autocomplete
 */

// Note: prefix and allowClear props are kept for interface compatibility but not used with PlaceAutocompleteElement
import { message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import config from '../../../config/environment.config';
import CustomInput from '../CustomInput/CustomInput';
import './GoogleMapsAutocomplete.scss';

export interface AddressComponents {
  street: string;
  city: string;
  state: string;
  zip: string;
  formattedAddress?: string; // Full formatted address from Google Places
  location?: {
    lat: number;
    long: number;
  };
}

export interface GoogleMapsAutocompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onPlaceSelected?: (place: AddressComponents) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  prefix?: React.ReactNode;
  allowClear?: boolean;
  types?: string[];
  componentRestrictions?: google.maps.places.ComponentRestrictions;
  error?: boolean;
  errorMessage?: string;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder = 'Search for an address',
  disabled = false,
  className,
  prefix: _prefix, // Not used with PlaceAutocompleteElement web component
  allowClear: _allowClear, // Not used with PlaceAutocompleteElement web component
  types = ['address'],
  componentRestrictions = { country: 'us' },
  error = false,
  errorMessage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteElementRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const legacyAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isInitializingRef = useRef(false);
  const apiErrorCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [apiError, setApiError] = useState(false);

  // Load Google Maps script with PlaceAutocompleteElement support
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const google = (window as any).google;
      if (google && google.maps && google.maps.places) {
        setScriptLoaded(true);
        return;
      }

      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        existingScript.addEventListener('load', () => setScriptLoaded(true));
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      // Using new Places API (New) - updated to use latest API version
      // The new Places API requires the 'places' library and uses the latest API version
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Check for API errors after a short delay
        setTimeout(() => {
          const googleMaps = (window as any).google?.maps;
          if (googleMaps && googleMaps.places) {
            setScriptLoaded(true);
            console.log('✅ Google Maps API loaded successfully');
          } else {
            // API loaded but Places API not available - likely an API key issue
            setApiError(true);
            message.error({
              content: 'Google Maps API configuration error. Please check API key settings or use manual address entry.',
              duration: 8,
            });
            console.error('❌ Google Maps Places API not available - check API key configuration');
          }
        }, 500);
      };
      script.onerror = () => {
        setApiError(true);
        message.error({
          content: 'Failed to load Google Maps. You can still enter your address manually.',
          duration: 6,
        });
        console.error('❌ Failed to load Google Maps script');
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  // Monitor for Google Maps API errors
  useEffect(() => {
    if (!scriptLoaded) return;

    // Check for Google error dialogs periodically
    const checkForErrors = () => {
      // Check for Google Maps error overlay/dialog
      const errorDialogs = document.querySelectorAll('[class*="gm-err"], [id*="gm-err"], [class*="gm-style-err"]');
      if (errorDialogs.length > 0) {
        setApiError(true);
        console.error('❌ Google Maps error dialog detected');
        // Don't show message here as Google already shows its own dialog
      }

      // Check if API is actually working
      const googleMaps = (window as any).google?.maps;
      if (googleMaps && !googleMaps.places) {
        setApiError(true);
      }
    };

    apiErrorCheckIntervalRef.current = setInterval(checkForErrors, 2000);

    return () => {
      if (apiErrorCheckIntervalRef.current) {
        clearInterval(apiErrorCheckIntervalRef.current);
      }
    };
  }, [scriptLoaded]);

  // Initialize autocomplete - try new API first, fallback to legacy
  useEffect(() => {
    const inputRefSnapshot = inputRef.current;
    const containerRefSnapshot = containerRef.current;

    if (!scriptLoaded || !containerRef.current || apiError) {
      return;
    }

    // Prevent multiple initializations
    if (legacyAutocompleteRef.current || isInitializingRef.current) {
      return; // Already initialized or initializing
    }

    // Set flag to prevent concurrent initializations
    isInitializingRef.current = true;

    // Wait a bit for the API to fully initialize
    const initTimer = setTimeout(() => {
      try {
        const googleMaps = (window as any).google?.maps;
        if (!googleMaps || !googleMaps.places) {
          console.error('❌ Google Maps Places API not loaded');
          setApiError(true);
          message.warning({
            content: 'Google Maps autocomplete is unavailable. You can still enter your address manually.',
            duration: 6,
          });
          isInitializingRef.current = false;
          return;
        }

        // Double-check we haven't initialized (race condition protection)
        if (legacyAutocompleteRef.current) {
          isInitializingRef.current = false;
          return; // Already initialized
        }

        // Get the input element
        let inputElement: HTMLInputElement | null = null;
        
        if (inputRef.current instanceof HTMLInputElement) {
          inputElement = inputRef.current;
        } else if (inputRef.current?.input) {
          inputElement = inputRef.current.input;
        } else if (inputRef.current?.querySelector) {
          inputElement = inputRef.current.querySelector('input');
        }

        if (!inputElement) {
          console.error('❌ Could not find HTML input element');
          isInitializingRef.current = false;
          return;
        }

        // Check if autocomplete is already attached to this input
        if ((inputElement as any).__googleMapsAutocomplete) {
          legacyAutocompleteRef.current = (inputElement as any).__googleMapsAutocomplete;
          isInitializingRef.current = false;
          return; // Already has autocomplete attached
        }

        // Use Places Autocomplete API (JavaScript API - this is the correct client-side API)
        // Note: The "Places API (New)" warning refers to server-side REST API, not the JavaScript API
        // For client-side, google.maps.places.Autocomplete is the recommended approach
        let autocomplete: google.maps.places.Autocomplete;
        try {
          autocomplete = new googleMaps.places.Autocomplete(inputElement, {
            types,
            componentRestrictions,
            fields: [
              'address_components',
              'formatted_address',
              'geometry',
              'place_id',
              'name',
              'types',
            ],
          });
        } catch (err: any) {
          // Catch API key errors or other initialization errors
          console.error('❌ Error creating Autocomplete:', err);
          setApiError(true);
          message.warning({
            content: 'Google Maps autocomplete failed to initialize. You can enter your address manually.',
            duration: 6,
          });
          isInitializingRef.current = false;
          return;
        }

        // Store reference on input element to prevent duplicates
        (inputElement as any).__googleMapsAutocomplete = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();

          if (!place.geometry || !place.address_components) {
            message.warning('Please select a complete address from the dropdown');
            return;
          }

          setLoading(true);

          try {
            // Log the place object to see what data is available
            console.log('🗺️ Place object received:', {
              formatted_address: place.formatted_address,
              address_components_count: place.address_components?.length || 0,
              place_id: place.place_id,
              types: place.types
            });

            const addressComponents = parseAddressComponents(
              place.address_components,
              place.geometry.location
            );

            // Add formatted address to components
            if (place.formatted_address) {
              addressComponents.formattedAddress = place.formatted_address;
              
              // Extract full street line from formatted address if street parsing didn't work well
              // Format: "123 Hyde St, San Francisco, CA 94102, USA"
              // We want to extract "123 Hyde St" (everything before the first comma)
              if (!addressComponents.street || addressComponents.street.split(' ').length < 2) {
                const streetMatch = place.formatted_address.match(/^([^,]+)/);
                if (streetMatch && streetMatch[1]) {
                  const fullStreet = streetMatch[1].trim();
                  // Only use if it looks like a street address (has number or is longer)
                  if (fullStreet.match(/\d/) || fullStreet.length > 5) {
                    addressComponents.street = fullStreet;
                    console.log('✅ Extracted full street from formatted address:', addressComponents.street);
                  }
                }
              }
            }

            // Fallback 1: Try to extract ZIP from formatted_address if not found in components
            if (!addressComponents.zip && place.formatted_address) {
              console.log('🔍 ZIP not found in components, trying formatted_address:', place.formatted_address);
              
              // Try multiple patterns for ZIP codes
              // US format: 12345 or 12345-6789
              let zipMatch = place.formatted_address.match(/\b(\d{5}(?:-\d{4})?)\b/);
              
              // If not found, try patterns like "State ZIP" or "ZIP Country"
              if (!zipMatch) {
                zipMatch = place.formatted_address.match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
                if (zipMatch && zipMatch[2]) {
                  zipMatch = [zipMatch[0], zipMatch[2]]; // Extract just the ZIP part
                }
              }
              
              // Try Canadian postal code format: A1A 1A1
              if (!zipMatch) {
                zipMatch = place.formatted_address.match(/\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/);
              }
              
              if (zipMatch) {
                addressComponents.zip = zipMatch[1] || zipMatch[0];
                console.log('✅ Extracted ZIP from formatted address:', addressComponents.zip);
              } else {
                console.warn('⚠️ Could not extract ZIP from formatted_address:', place.formatted_address);
              }
            }

            // Validate that we have the essential address components
            if (!addressComponents.street && !addressComponents.city) {
              message.warning('Unable to extract complete address. Please try a different location.');
              setLoading(false);
              return;
            }

            // Update input value with formatted address
            const formattedAddress = place.formatted_address || addressComponents.street;
            setInputValue(formattedAddress);
            if (onChange) {
              onChange(formattedAddress);
            }

            // Final check: Log the complete address data before sending to callback
            console.log('📋 Final address components before callback:', {
              street: addressComponents.street || '(empty)',
              city: addressComponents.city || '(empty)',
              state: addressComponents.state || '(empty)',
              zip: addressComponents.zip || '(empty)',
              location: addressComponents.location ? 'present' : 'missing'
            });

            // Trigger callback with parsed address
            if (onPlaceSelected) {
              onPlaceSelected(addressComponents);
            }
            
            // Only show warning if ZIP code is missing
            if (!addressComponents.zip) {
              message.warning('ZIP code not found. Please enter it manually.', 4);
            }
          } catch (error) {
            console.error('Error parsing address:', error);
            message.error('Failed to parse address. Please try again.');
          } finally {
            setLoading(false);
          }
        });

        legacyAutocompleteRef.current = autocomplete;
        isInitializingRef.current = false;
        console.log('✅ Google Maps Autocomplete initialized (legacy API)');

      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        message.error('Failed to initialize location search. Please refresh the page.');
        isInitializingRef.current = false;
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
      isInitializingRef.current = false;
      // Cleanup on unmount
      if (legacyAutocompleteRef.current) {
        // Get the input element to clean up the reference
        let inputElement: HTMLInputElement | null = null;
        if (inputRefSnapshot instanceof HTMLInputElement) {
          inputElement = inputRefSnapshot;
        } else if ((inputRefSnapshot as any)?.input) {
          inputElement = (inputRefSnapshot as any).input;
        } else if ((inputRefSnapshot as any)?.querySelector) {
          inputElement = (inputRefSnapshot as any).querySelector('input');
        }
        
        if (inputElement) {
          delete (inputElement as any).__googleMapsAutocomplete;
        }
        
        legacyAutocompleteRef.current = null;
      }
      if (autocompleteElementRef.current && containerRefSnapshot) {
        try {
          containerRefSnapshot.removeChild(autocompleteElementRef.current);
        } catch (e) {
          // Element might already be removed
        }
        autocompleteElementRef.current = null;
      }
    };
  }, [apiError, componentRestrictions, onChange, onPlaceSelected, scriptLoaded, types]); // Re-run when config changes too

  const parseAddressComponents = (
    components: google.maps.GeocoderAddressComponent[],
    location?: google.maps.LatLng
  ): AddressComponents => {
    const addressData: AddressComponents = {
      street: '',
      city: '',
      state: '',
      zip: '',
    };

    let streetNumber = '';
    let route = '';
    let premise = '';
    let subpremise = '';

    console.log('🔍 Parsing address components:', components);
    
    // Debug: Log all components to help identify zip code issues
    console.log('🔍 All address components:', components.map(c => ({
      types: c.types,
      short_name: c.short_name,
      long_name: c.long_name
    })));
    
    const postalCodeComponents = components.filter(c => 
      c.types.includes('postal_code') || c.types.includes('postal_code_prefix')
    );
    if (postalCodeComponents.length > 0) {
      console.log('📮 Found postal code components:', postalCodeComponents.map(c => ({
        types: c.types,
        short_name: c.short_name,
        long_name: c.long_name,
        hasValue: !!(c.long_name || c.short_name)
      })));
    } else {
      console.warn('⚠️ No postal code components found in address');
      console.log('💡 Tip: ZIP code might be in formatted_address or need manual entry');
    }

    components.forEach((component) => {
      const types = component.types;

      // Street components - capture all street-related fields
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('premise')) {
        premise = component.long_name;
      }
      if (types.includes('subpremise')) {
        subpremise = component.long_name;
      }

      // City - try multiple types in order of preference
      if (types.includes('locality')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('sublocality')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('sublocality_level_1')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('postal_town')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('administrative_area_level_3')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('administrative_area_level_2')) {
        addressData.city = component.long_name;
      }

      // State/Province - prefer short_name (2-letter code) for US states
      if (types.includes('administrative_area_level_1')) {
        // Use short_name for state codes (e.g., "CA", "NY"), fallback to long_name
        addressData.state = component.short_name || component.long_name;
      }

      // Postal Code - try multiple approaches
      // Sometimes postal code is in short_name, sometimes in long_name
      // Also check for postal_code_prefix for extended zip codes
      if (types.includes('postal_code')) {
        // Try both long_name and short_name - sometimes Google uses short_name for ZIP codes
        const zipValue = component.long_name || component.short_name;
        if (zipValue && zipValue.trim()) {
          addressData.zip = zipValue.trim();
          console.log('✅ Found ZIP code in postal_code:', {
            long_name: component.long_name,
            short_name: component.short_name,
            selected: addressData.zip
          });
        }
      } else if (types.includes('postal_code_prefix')) {
        // Some countries use postal_code_prefix (e.g., UK postcodes)
        const zipValue = component.long_name || component.short_name;
        if (zipValue && zipValue.trim() && !addressData.zip) {
          addressData.zip = zipValue.trim();
          console.log('✅ Found ZIP code in postal_code_prefix:', addressData.zip);
        }
      }
    });

    // Build street address with all available components
    const streetParts = [];
    if (streetNumber) streetParts.push(streetNumber);
    if (route) streetParts.push(route);
    if (premise && !streetNumber) streetParts.push(premise);
    if (subpremise) streetParts.push(subpremise);
    
    if (streetParts.length > 0) {
      addressData.street = streetParts.join(' ').trim();
    }

    // Add coordinates if available - always capture location when geometry is present
    if (location) {
      const lat = location.lat();
      const lng = location.lng();
      
      // Validate coordinates are within valid ranges
      // lat: -90 to 90, long: -180 to 180
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        addressData.location = {
          lat: Number(lat.toFixed(6)), // Round to 6 decimal places for precision
          long: Number(lng.toFixed(6)),
        };
      } else {
        console.warn('⚠️ Invalid coordinates detected:', { lat, lng });
      }
    }

    // If zip is still empty, try to extract from formatted address or other sources
    if (!addressData.zip && location) {
      // Sometimes we need to use Geocoding API to get postal code
      // For now, log a warning
      console.warn('⚠️ ZIP code not found in address components. You may need to enter it manually.');
    }

    console.log('✅ Parsed address data:', {
      street: addressData.street || '(not found)',
      city: addressData.city || '(not found)',
      state: addressData.state || '(not found)',
      zip: addressData.zip || '(not found)',
      location: addressData.location ? 
        `lat: ${addressData.location.lat}, long: ${addressData.location.long}` : 
        'not available'
    });

    return addressData;
  };

  // Sync external value changes
  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  // Show helpful message if API error but allow manual input
  const displayError = apiError && errorMessage ? errorMessage : 
    (apiError ? 'Google Maps autocomplete unavailable. Please enter address manually.' : errorMessage);

  return (
    <div 
      className={`google-maps-autocomplete ${className || ''}`} 
      ref={containerRef}
      data-api-error={apiError}
    >
      <CustomInput
        ref={inputRef}
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={_allowClear}
        error={error || apiError}
        errorMessage={displayError}
        suffix={
          loading ? (
            <span style={{ fontSize: '12px', color: 'var(--color-primary, #2563eb)' }}>Loading...</span>
          ) : !scriptLoaded && !apiError ? (
            <span style={{ fontSize: '12px', color: '#999' }}>Loading maps...</span>
          ) : apiError ? (
            <span style={{ fontSize: '12px', color: '#ff4d4f' }} title="Autocomplete unavailable">⚠️</span>
          ) : undefined
        }
      />
      {apiError && (
        <div style={{ 
          marginTop: '4px', 
          fontSize: '12px', 
          color: '#8c8c8c',
          fontStyle: 'italic'
        }}>
          You can still enter your address manually. Autocomplete will work once API is configured.
        </div>
      )}
    </div>
  );
};

export default GoogleMapsAutocomplete;

